import React, { useState, useEffect } from 'react';
import { Link, useParams } from 'react-router-dom';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { ArrowLeft, Calendar, Clock, ExternalLink } from 'lucide-react';

const PageContainer = styled.div`
  min-height: 100vh;
  background: #474545;
  padding: 100px 20px 60px;
`;

const Container = styled.div`
  max-width: 800px;
  margin: 0 auto;
`;

const BackLink = styled(Link)`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  color: #0097B2;
  text-decoration: none;
  font-weight: 600;
  margin-bottom: 40px;
  transition: all 0.3s ease;

  &:hover {
    transform: translateX(-5px);
    color: #FAECEC;
  }
`;

const HeroImage = styled.div`
  width: 100%;
  height: 400px;
  background: ${props => props.src 
    ? `url(${props.src}) center/cover no-repeat` 
    : 'linear-gradient(135deg, #0097B2 0%, #007a94 100%)'};
  border-radius: 20px;
  margin-bottom: 40px;
  position: relative;
  overflow: hidden;

  &::after {
    content: '';
    position: absolute;
    bottom: 0;
    left: 0;
    right: 0;
    height: 100px;
    background: linear-gradient(to top, rgba(71, 69, 69, 0.8) 0%, transparent 100%);
  }

  @media (max-width: 768px) {
    height: 250px;
  }
`;

const ArticleHeader = styled(motion.header)`
  margin-bottom: 40px;
`;

const ArticleMeta = styled.div`
  display: flex;
  align-items: center;
  gap: 20px;
  margin-bottom: 20px;
  flex-wrap: wrap;
`;

const MetaItem = styled.span`
  display: flex;
  align-items: center;
  gap: 8px;
  color: #0097B2;
  font-size: 0.95rem;
  font-weight: 500;
`;

const Title = styled.h1`
  font-size: 2.5rem;
  font-weight: 700;
  color: #FAECEC;
  line-height: 1.3;
  margin-bottom: 16px;

  @media (max-width: 768px) {
    font-size: 1.75rem;
  }
`;

const Description = styled.p`
  color: #FAECEC;
  opacity: 0.8;
  font-size: 1.1rem;
  line-height: 1.6;
`;

const ArticleContent = styled.article`
  color: #FAECEC;
  font-size: 1.05rem;
  line-height: 1.8;

  h1, h2, h3, h4, h5, h6 {
    color: #FAECEC;
    margin: 32px 0 16px;
    font-weight: 600;
  }

  h1 { font-size: 2rem; }
  h2 { font-size: 1.75rem; }
  h3 { font-size: 1.5rem; }
  h4 { font-size: 1.25rem; }

  p {
    margin-bottom: 20px;
    opacity: 0.9;
  }

  a {
    color: #0097B2;
    text-decoration: underline;
    transition: color 0.3s ease;

    &:hover {
      color: #FAECEC;
    }
  }

  ul, ol {
    margin: 20px 0;
    padding-left: 24px;
  }

  li {
    margin-bottom: 10px;
  }

  blockquote {
    border-left: 4px solid #0097B2;
    padding-left: 20px;
    margin: 24px 0;
    font-style: italic;
    opacity: 0.85;
  }

  code {
    background: rgba(0, 151, 178, 0.15);
    padding: 2px 8px;
    border-radius: 4px;
    font-family: 'Courier New', monospace;
    font-size: 0.9em;
  }

  pre {
    background: rgba(0, 0, 0, 0.3);
    padding: 20px;
    border-radius: 12px;
    overflow-x: auto;
    margin: 24px 0;

    code {
      background: none;
      padding: 0;
    }
  }

  img {
    max-width: 100%;
    height: auto;
    border-radius: 12px;
    margin: 24px 0;
  }

  table {
    width: 100%;
    border-collapse: collapse;
    margin: 24px 0;

    th, td {
      padding: 12px;
      border: 1px solid rgba(0, 151, 178, 0.2);
      text-align: left;
    }

    th {
      background: rgba(0, 151, 178, 0.1);
      font-weight: 600;
    }
  }
`;

const SourceLink = styled.a`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  color: #0097B2;
  font-weight: 600;
  margin-top: 40px;
  padding: 12px 20px;
  background: rgba(0, 151, 178, 0.1);
  border: 1px solid rgba(0, 151, 178, 0.3);
  border-radius: 50px;
  text-decoration: none;
  transition: all 0.3s ease;

  &:hover {
    background: #0097B2;
    color: #FAECEC;
  }
`;

const LoadingContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 400px;
`;

const LoadingSpinner = styled(motion.div)`
  width: 50px;
  height: 50px;
  border: 3px solid rgba(0, 151, 178, 0.2);
  border-top-color: #0097B2;
  border-radius: 50%;
`;

const NotFound = styled.div`
  text-align: center;
  padding: 80px 20px;
`;

const NotFoundTitle = styled.h2`
  font-size: 2rem;
  color: #FAECEC;
  margin-bottom: 16px;
`;

const NotFoundText = styled.p`
  color: #FAECEC;
  opacity: 0.7;
  margin-bottom: 24px;
`;

const formatDate = (dateString) => {
  try {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  } catch {
    return dateString;
  }
};

const estimateReadTime = (html) => {
  if (!html) return '3 min read';
  const text = html.replace(/<[^>]*>/g, '');
  const words = text.split(/\s+/).length;
  const minutes = Math.ceil(words / 200);
  return `${minutes} min read`;
};

const BlogArticle = () => {
  const { slug } = useParams();
  const [article, setArticle] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchArticle = async () => {
      try {
        const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/articles/${slug}`);
        if (!response.ok) {
          if (response.status === 404) {
            setError('not_found');
          } else {
            throw new Error('Failed to fetch article');
          }
          return;
        }
        const data = await response.json();
        setArticle(data);
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchArticle();
  }, [slug]);

  if (loading) {
    return (
      <PageContainer>
        <Container>
          <LoadingContainer>
            <LoadingSpinner
              animate={{ rotate: 360 }}
              transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
            />
          </LoadingContainer>
        </Container>
      </PageContainer>
    );
  }

  if (error === 'not_found' || !article) {
    return (
      <PageContainer>
        <Container>
          <BackLink to="/blog" data-testid="back-to-blog">
            <ArrowLeft size={20} />
            Back to Blog
          </BackLink>
          <NotFound data-testid="not-found">
            <NotFoundTitle>Article Not Found</NotFoundTitle>
            <NotFoundText>
              The article you're looking for doesn't exist or has been removed.
            </NotFoundText>
            <Link to="/blog" style={{ color: '#0097B2', fontWeight: 600 }}>
              Browse all articles
            </Link>
          </NotFound>
        </Container>
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      <Container>
        <BackLink to="/blog" data-testid="back-to-blog">
          <ArrowLeft size={20} />
          Back to Blog
        </BackLink>

        {article.heroImageUrl && (
          <HeroImage src={article.heroImageUrl} data-testid="hero-image" />
        )}

        <ArticleHeader
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
        >
          <ArticleMeta>
            <MetaItem data-testid="article-date">
              <Calendar size={16} />
              {formatDate(article.createdAt)}
            </MetaItem>
            <MetaItem data-testid="article-read-time">
              <Clock size={16} />
              {estimateReadTime(article.content_html)}
            </MetaItem>
          </ArticleMeta>
          <Title data-testid="article-title">{article.title}</Title>
          <Description data-testid="article-description">
            {article.metaDescription}
          </Description>
        </ArticleHeader>

        <ArticleContent
          data-testid="article-content"
          dangerouslySetInnerHTML={{ __html: article.content_html }}
        />

        {article.publicUrl && (
          <SourceLink 
            href={article.publicUrl}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="source-link"
          >
            <ExternalLink size={16} />
            View Original Source
          </SourceLink>
        )}
      </Container>
    </PageContainer>
  );
};

export default BlogArticle;
